/**
 * Siatka kafli /realizacje, pogrupowana po `status` (wdrozenia najpierw).
 *
 * Wzorzec animacji i szerokosc kontenera skopiowane z SelectedWorks.tsx, zeby strona
 * czytala sie jak reszta serwisu (useInView + stagger, max-w-[1200px]).
 *
 * WSZYSTKIE LICZBY SA LICZONE Z TABLICY, nie wpisane recznie - patrz komentarz
 * w src/data/realizacje.ts.
 */
import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import ProjectTile from './ui/project-tile';
import { PROJECTS, GROUPS, type ProjectStatus } from '../data/realizacje';

/** Ile projektow ma dany status. Zrodlo liczb w naglowku sekcji. */
export function countByStatus(status: ProjectStatus) {
  return PROJECTS.filter((p) => p.status === status).length;
}

/** Ile projektow da sie kliknac bez logowania. Glowna liczba w hero. */
export function countClickable() {
  return PROJECTS.filter((p) => p.access === 'klikalne').length;
}

function Group({ status, heading, sub }: { status: ProjectStatus; heading: string; sub: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const items = PROJECTS.filter((p) => p.status === status);

  if (items.length === 0) return null;

  return (
    <div className="mb-16 md:mb-24" ref={ref}>
      <motion.div
        className="mb-10"
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
      >
        {/* <h2>, nie <span> - inaczej kolejnosc naglowkow skacze h1 -> h3 (Lighthouse a11y). */}
        <div className="flex items-center gap-4 mb-5">
          <div className="w-8 h-px bg-stroke" />
          <h2 className="text-xs text-muted uppercase tracking-[0.3em] font-normal">
            {heading} ({items.length})
          </h2>
        </div>
        <p className="text-muted max-w-2xl leading-relaxed">{sub}</p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((project, i) => (
          <motion.div
            key={project.id}
            initial={{ opacity: 0, y: 24 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
            transition={{ duration: 0.6, delay: Math.min(i * 0.08, 0.4), ease: 'easeOut' }}
          >
            <ProjectTile project={project} />
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export default function RealizacjeGrid() {
  return (
    <section
      id="realizacje-lista"
      aria-label="Lista realizacji i prototypów"
      className="bg-bg py-8 md:py-12"
    >
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-16">
        {GROUPS.map((g) => (
          <Group key={g.status} status={g.status} heading={g.heading} sub={g.sub} />
        ))}
      </div>
    </section>
  );
}
